import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import styles from './MainMenu.module.scss';
import useMainMenu from '@/admin/hooks/useMainMenu';
import { useSchoolDetails } from '@/school-management/hooks/useSchoolDetails';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { schoolDetailsData } from '@/admin/config/listConfig';
import { ROUTES } from '@/admin/config/routes';

type MainMenuProps = {
  label?: string;
  showOnlyFirst?: boolean;
  defaultState?: 'hover' | 'active' | 'pressed';
  isCollapsed?: boolean;
};

const MainMenu: React.FC<MainMenuProps> = ({
  label,
  showOnlyFirst = false,
  defaultState,
  isCollapsed = false,
}) => {
  const { menuItems, isInSchoolContext, currentSchoolId } = useMainMenu();
  const { data: schoolApiData } = useSchoolDetails(currentSchoolId || undefined);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cameFromMainMenu, setCameFromMainMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const itemsToDisplay = showOnlyFirst ? [menuItems[0]] : menuItems;

  const currentSchoolName =
    schoolApiData?.data?.data?.school?.name ||
    (currentSchoolId ? schoolDetailsData.find(s => s.id === currentSchoolId)?.name : null);

  useEffect(() => {
    const hasMainMenuItems = menuItems.some(
      item =>
        item.path === ROUTES.ACCOUNTS || item.path === '/districts' || item.path === '/schools'
    );

    if (hasMainMenuItems && !isInSchoolContext) {
      setCameFromMainMenu(true);
    } else if (!isInSchoolContext && !hasMainMenuItems) {
      setCameFromMainMenu(false);
    }
  }, [location.pathname, menuItems, isInSchoolContext]);

  const getSearchWithTabOnly = (search: string): string => {
    const params = new URLSearchParams(search);
    const tab = params.get('tab');

    return tab ? `?tab=${tab}` : '';
  };

  const isMenuItemActive = (itemPath: string) => {
    const currentPath = location.pathname + getSearchWithTabOnly(location.search);

    // For school context, temp solution before we finalize the menu structure
    if (isInSchoolContext && currentSchoolId) {
      const tabMatch = itemPath.match(/\?tab=(\w+)/);
      if (tabMatch) {
        const tab = tabMatch[1];
        const currentPathOnly = location.pathname;

        const tabSubRoutes = {
          classes: ['create-class', 'edit-class', 'class/'],
          teachers: ['edit-teacher'],
        };

        const subRoutes = tabSubRoutes[tab as keyof typeof tabSubRoutes];
        if (subRoutes && subRoutes.some(route => currentPathOnly.includes(`/${route}`))) {
          return true;
        }
      }
    }

    return currentPath === itemPath;
  };

  const handleBackClick = () => {
    navigate(ROUTES.SCHOOLS);
    setCameFromMainMenu(true);
  };

  return (
    <>
      {isInSchoolContext && cameFromMainMenu && (
        <div className={styles.backButton} onClick={handleBackClick}>
          <AppIcon icon='ArrowLeft' style={{ width: '24px', height: '24px' }} />
          <span className={styles.schoolName}>{currentSchoolName}</span>
        </div>
      )}

      <main className={clsx(styles.mainMenu, { [styles.collapsed]: isCollapsed })}>
        <ul className={clsx(styles.mainMenuWrapper, { [styles.collapsed]: isCollapsed })}>
          {itemsToDisplay.map((item, index) => {
            const isHovered = hoveredIndex === index || defaultState === 'hover';
            const isActiveDefault = defaultState === 'active';
            const isPressed = defaultState === 'pressed';
            const isActive = isMenuItemActive(item.path);

            return (
              <div
                className={clsx(styles.mainMenuItem)}
                key={index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <NavLink
                  to={item.path}
                  className={() =>
                    clsx({
                      [styles.isActive]: isActive || isActiveDefault,
                      [styles.isHovered]: isHovered,
                      [styles.isPressed]: isPressed,
                    })
                  }
                  title={isCollapsed ? item.label : undefined}
                >
                  <>
                    <AppIcon
                      icon={
                        isActive || isActiveDefault || isHovered || isPressed
                          ? item.iconNameActive
                          : item.iconName
                      }
                      className={clsx({
                        [styles.iconActive]: isActive || isActiveDefault || isHovered || isPressed,
                      })}
                    />
                    {!isCollapsed && <span className={styles.menuLabel}>{item.label}</span>}
                  </>
                </NavLink>
              </div>
            );
          })}
        </ul>
      </main>
    </>
  );
};

export default MainMenu;
